import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';

const ERPSpreadsheet = ({ 
  data = [], 
  itemConfig = {}, 
  loading = false,
  metricType = 'quantity_percentage',
  percentageType = 'total_sales'
}) => {
  const [processedData, setProcessedData] = useState({});
  
  // Extract unique values for grid dimensions
  const { types, shapes, sizes, brims } = useMemo(() => {
    const typesSet = new Set();
    const shapesSet = new Set();
    const sizesSet = new Set();
    const brimsSet = new Set();
    
    // Get from itemConfig if available
    if (itemConfig.types) {
      itemConfig.types.forEach(type => typesSet.add(type.type_name));
    }
    if (itemConfig.shapes) {
      itemConfig.shapes.forEach(shape => shapesSet.add(shape.shape_name));
    }
    if (itemConfig.sizes) {
      itemConfig.sizes.forEach(size => sizesSet.add(size.size_name));
    }
    if (itemConfig.brims) {
      itemConfig.brims.forEach(brim => brimsSet.add(brim.brim_name));
    }
    
    // Also get from data
    data.forEach(item => {
      if (item.type_name) typesSet.add(item.type_name);
      if (item.shape_name) shapesSet.add(item.shape_name);
      if (item.size_name) sizesSet.add(item.size_name);
      if (item.brim_name) brimsSet.add(item.brim_name);
    });
    
    return {
      types: Array.from(typesSet).sort(),
      shapes: Array.from(shapesSet).sort(),
      sizes: Array.from(sizesSet).sort(),
      brims: Array.from(brimsSet).sort()
    };
  }, [data, itemConfig]);

  // Process data into grid format
  useEffect(() => {
    const processed = {};
    
    data.forEach(item => {
      const key = `${item.type_name}-${item.shape_name}-${item.size_name}-${item.brim_name}`;
      processed[key] = {
        ...item,
        displayValue: getDisplayValue(item)
      };
    });
    
    setProcessedData(processed);
  }, [data, metricType]);

  const getDisplayValue = (item) => {
    switch (metricType) {
      case 'quantity_percentage':
        return `${(item.quantity_percentage || 0).toFixed(1)}%`;
      case 'revenue_percentage':
        return `${(item.revenue_percentage || 0).toFixed(1)}%`;
      case 'raw_quantity':
        return (item.total_quantity || 0).toLocaleString();
      case 'raw_revenue':
        return `$${(item.total_revenue || 0).toLocaleString()}`;
      default:
        return (item.total_quantity || 0).toLocaleString();
    }
  };

  const getCellValue = (type, shape, size, brim) => {
    const key = `${type}-${shape}-${size}-${brim}`;
    return processedData[key];
  };

  const getCellColor = (value) => {
    if (!value) return '#f5f5f5';
    
    const percentage = metricType.includes('percentage') 
      ? (metricType === 'quantity_percentage' ? value.quantity_percentage : value.revenue_percentage)
      : 0;
    
    if (metricType.includes('percentage')) {
      // Color intensity based on percentage
      if (percentage >= 5) return '#4caf50'; // Green for high
      if (percentage >= 2) return '#8bc34a'; // Light green
      if (percentage >= 1) return '#cddc39'; // Lime
      if (percentage >= 0.5) return '#ffeb3b'; // Yellow
      if (percentage >= 0.1) return '#ffc107'; // Amber
      return '#ff9800'; // Orange for low but present
    }
    
    return '#e3f2fd'; // Light blue for raw values
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading sales data...
        </Typography>
      </Box>
    );
  }

  if (!types.length || !shapes.length || !sizes.length || !brims.length) {
    return (
      <Box textAlign="center" p={4}>
        <Typography variant="h6" color="textSecondary">
          No data available to display
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Legend */}
      <Box mb={2}>
        <Typography variant="h6" gutterBottom>
          Sales Data Grid
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Chip label={`Showing: ${metricType.replace('_', ' ')}`} color="primary" size="small" />
          <Chip label={`Analysis: ${percentageType.replace('_', ' ')}`} color="secondary" size="small" />
          <Typography variant="caption" color="textSecondary">
            Rows: Types × Shapes | Columns: Sizes × Brims
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  backgroundColor: '#1976d2', 
                  color: 'white',
                  fontWeight: 'bold',
                  minWidth: 120,
                  position: 'sticky',
                  left: 0,
                  zIndex: 3
                }}
              >
                Type / Shape
              </TableCell>
              {sizes.map(size => 
                brims.map(brim => (
                  <TableCell 
                    key={`${size}-${brim}`}
                    align="center"
                    sx={{ 
                      backgroundColor: '#1976d2', 
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 100,
                      writingMode: 'horizontal-tb'
                    }}
                  >
                    <Box>
                      <Typography variant="caption" display="block">
                        {size}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                        {brim}
                      </Typography>
                    </Box>
                  </TableCell>
                ))
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map(type => 
              shapes.map(shape => (
                <TableRow key={`${type}-${shape}`} hover>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '2px solid #ddd'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {type}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {shape}
                      </Typography>
                    </Box>
                  </TableCell>
                  {sizes.map(size => 
                    brims.map(brim => {
                      const cellData = getCellValue(type, shape, size, brim);
                      const backgroundColor = getCellColor(cellData);
                      
                      return (
                        <TableCell 
                          key={`${size}-${brim}`}
                          align="center"
                          sx={{ 
                            backgroundColor,
                            cursor: cellData ? 'pointer' : 'default',
                            transition: 'all 0.2s',
                            '&:hover': cellData ? {
                              backgroundColor: '#bbdefb',
                              transform: 'scale(1.05)'
                            } : {}
                          }}
                        >
                          {cellData ? (
                            <Tooltip 
                              title={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    {type} - {shape} - {size} - {brim}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Quantity: {cellData.total_quantity?.toLocaleString() || 0}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Revenue: ${cellData.total_revenue?.toLocaleString() || 0}
                                  </Typography>
                                  {cellData.quantity_percentage && (
                                    <Typography variant="caption" display="block">
                                      Qty %: {cellData.quantity_percentage.toFixed(2)}%
                                    </Typography>
                                  )}
                                  {cellData.revenue_percentage && (
                                    <Typography variant="caption" display="block">
                                      Rev %: {cellData.revenue_percentage.toFixed(2)}%
                                    </Typography>
                                  )}
                                </Box>
                              }
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {cellData.displayValue}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box mt={2}>
        <Typography variant="caption" color="textSecondary">
          Total combinations: {types.length * shapes.length * sizes.length * brims.length} | 
          Items with data: {Object.keys(processedData).length} |
          Coverage: {((Object.keys(processedData).length / (types.length * shapes.length * sizes.length * brims.length)) * 100).toFixed(1)}%
        </Typography>
      </Box>
    </Box>
  );
};

export default ERPSpreadsheet; 